import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles, Shield, Wrench, Megaphone, Star, Search, Pin,
} from 'lucide-react';
import { format } from 'date-fns';
import { MarkdownBrief } from '@/components/ontology/OntologyHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  feature:      { label: 'New',      color: 'bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/30',      Icon: Sparkles },
  improvement:  { label: 'Improved', color: 'bg-[#AF52DE]/10 text-[#AF52DE] border-[#AF52DE]/30',      Icon: Star },
  fix:          { label: 'Fix',      color: 'bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30',      Icon: Wrench },
  security:     { label: 'Security', color: 'bg-[#FF3B30]/10 text-[#FF3B30] border-[#FF3B30]/30',      Icon: Shield },
  announcement: { label: 'News',     color: 'bg-[#FF9500]/10 text-[#FF9500] border-[#FF9500]/30',      Icon: Megaphone },
};

const WhatsNewPage = () => {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/changelog`, { headers, params: { limit: 100 } });
        setEntries(r.data.entries || []);
      } finally {
        setLoading(false);
        // Jump to anchor if present
        if (window.location.hash) {
          setTimeout(() => {
            const el = document.getElementById(window.location.hash.slice(1));
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 150);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter(e => {
      if (filter !== 'all' && e.category !== filter) return false;
      if (!q) return true;
      return (
        e.title.toLowerCase().includes(q) ||
        e.summary.toLowerCase().includes(q) ||
        (e.body_markdown || '').toLowerCase().includes(q)
      );
    });
  }, [entries, query, filter]);

  return (
    <div className="max-w-3xl mx-auto p-4 lg:p-0 space-y-4" data-testid="whats-new-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-6 h-6 text-[#002FA7]" />
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">What's New</h1>
        </div>
        <p className="text-sm text-[#4B5563]">
          Everything we've shipped in KStreak — from brand-new modules to small quality-of-life improvements.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="whats-new-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search releases…"
            className="pl-8 h-9 text-sm"
            data-testid="whats-new-search"
          />
        </div>
        <div className="flex items-center gap-1">
          {['all', 'feature', 'improvement', 'security', 'fix', 'announcement'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                filter === f
                  ? 'bg-[#002FA7] text-white border-[#002FA7]'
                  : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#9CA3AF]'
              }`}
              data-testid={`whats-new-filter-${f}`}
            >
              {f === 'all' ? 'All' : CATEGORY_META[f]?.label || f}
            </button>
          ))}
        </div>
      </div>

      {/* Entries */}
      {loading && <p className="text-sm text-[#9CA3AF]">Loading…</p>}
      {!loading && filtered.length === 0 && (
        <Card className="border-dashed border-[#E5E7EB]"><CardContent className="p-8 text-center">
          <Sparkles className="w-10 h-10 mx-auto text-[#E5E7EB] mb-2" />
          <p className="text-sm text-[#9CA3AF]">No entries match your filters.</p>
        </CardContent></Card>
      )}
      <div className="space-y-3" data-testid="whats-new-list">
        {filtered.map(e => {
          const meta = CATEGORY_META[e.category] || CATEGORY_META.feature;
          return (
            <Card
              key={e.entry_id}
              id={e.entry_id}
              className="border-[#E5E7EB] scroll-mt-20"
              data-testid={`whats-new-entry-${e.entry_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.color.split(' ')[0]}`}>
                    <meta.Icon className={`w-4 h-4 ${meta.color.split(' ').find(c => c.startsWith('text-')) || 'text-[#002FA7]'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <Badge className={`text-[10px] border ${meta.color}`}>{meta.label}</Badge>
                      {e.is_pinned && (
                        <Badge variant="outline" className="text-[10px] text-[#FF9500] border-[#FF9500]/40 gap-0.5">
                          <Pin className="w-2.5 h-2.5" />Pinned
                        </Badge>
                      )}
                      <span className="text-[11px] text-[#9CA3AF] ml-auto">
                        {e.published_at ? format(new Date(e.published_at), 'MMM d, yyyy') : ''}
                      </span>
                    </div>
                    <h2 className="text-base font-semibold text-[#0A0A0A] leading-snug">{e.title}</h2>
                    <p className="text-sm text-[#4B5563] mt-1">{e.summary}</p>
                    {e.body_markdown && (
                      <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
                        <MarkdownBrief markdown={e.body_markdown} />
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default WhatsNewPage;
